import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Landmark, TrendingUp, BarChart2, Clock, Check,
  Loader2, ChevronDown, Sprout, AlertCircle, Key, Copy, CheckCircle2, RefreshCw, ShieldAlert, SlidersHorizontal, Percent, ArrowRightLeft,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth.store';
import { extractApiError } from '../lib/api-error';
import { formatDate, formatDateTime } from '../lib/formatters';

const inputCls = 'w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/40 transition-colors placeholder-slate-700';

// SVG donut chart: reserve (emerald) / circulating (purple) / revenue (rose)
function ReserveDonutChart({ reserve }: { reserve: Record<string, string | number | null> }) {
  const total    = Number(reserve.totalSupply)       || 1;
  const res      = Number(reserve.reserveBalance)    || 0;
  const circ     = Number(reserve.circulatingSupply) || 0;
  const rev      = Number(reserve.cbRevenueBalance)  || 0;

  const r = 60; const cx = 80; const cy = 80;
  const circumference = 2 * Math.PI * r;

  function arc(value: number, offsetPct: number, color: string) {
    const pct = value / total;
    const dash = pct * circumference;
    const gap  = circumference - dash;
    const offset = circumference - offsetPct * circumference;
    return (
      <circle
        r={r} cx={cx} cy={cy}
        fill="none" stroke={color} strokeWidth={18}
        strokeDasharray={`${dash} ${gap}`}
        strokeDashoffset={offset}
        strokeLinecap="butt"
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
    );
  }

  const resPct  = (res  / total * 100).toFixed(1);
  const circPct = (circ / total * 100).toFixed(1);
  const revPct  = (rev  / total * 100).toFixed(1);

  const fmt = (n: number) =>
    n >= 1e12 ? `${(n/1e12).toFixed(2)}T`
    : n >= 1e9  ? `${(n/1e9).toFixed(2)}B`
    : n >= 1e6  ? `${(n/1e6).toFixed(2)}M`
    : n.toLocaleString();

  return (
    <div className="flex flex-col sm:flex-row items-center gap-6 py-2">
      <svg width={160} height={160} style={{ transform: 'rotate(-90deg)' }}>
        {/* bg track */}
        <circle r={r} cx={cx} cy={cy} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={18} />
        {arc(res,  0,                    '#10b981')}
        {arc(circ, res / total,          '#a855f7')}
        {arc(rev,  (res + circ) / total, '#f43f5e')}
        {/* inner label */}
        <text x={cx} y={cy + 6} textAnchor="middle" style={{ transform: 'rotate(90deg)', transformOrigin: `${cx}px ${cy}px`, fill: '#94a3b8', fontSize: 10 }}>
          {fmt(total)} UKC
        </text>
      </svg>
      <div className="flex flex-col gap-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: '#10b981' }} />
          <span className="text-slate-400">Reserve</span>
          <span className="ml-auto font-semibold text-emerald-400">{resPct}%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: '#a855f7' }} />
          <span className="text-slate-400">Circulating</span>
          <span className="ml-auto font-semibold text-purple-400">{circPct}%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: '#f43f5e' }} />
          <span className="text-slate-400">CB Revenue</span>
          <span className="ml-auto font-semibold text-rose-400">{revPct}%</span>
        </div>
        <div className="mt-1 pt-2 border-t border-white/[0.06] text-slate-600">
          Total: {fmt(total)} UKC
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string | number; sub?: string;
  icon: React.ElementType; color: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-600 mt-1">{sub}</p>}
    </motion.div>
  );
}

function WorldKeyRegenSection({ worlds }: { worlds: { id: string; name: string; currencySymbol: string }[] }) {
  const [selectedWorldId, setSelectedWorldId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [newKeys, setNewKeys] = useState<{ apiKey: string; hmacSecret: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const regenMutation = useMutation({
    mutationFn: (worldId: string) =>
      api.post(`/worlds/${worldId}/regenerate-keys`).then((r) => r.data) as Promise<{ apiKey: string; hmacSecret: string }>,
    onSuccess: (data) => { setNewKeys(data); setConfirmed(false); },
  });

  const copyToClipboard = (val: string, key: string) => {
    void navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleReset = () => { setNewKeys(null); setSelectedWorldId(''); setConfirmed(false); };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="glass-card overflow-hidden"
      style={{ borderColor: 'rgba(239,68,68,0.15)' }}
    >
      <div className="h-px bg-gradient-to-r from-transparent via-red-500/20 to-transparent" />
      <div className="px-5 py-4 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-red-400" />
        <h3 className="text-sm font-semibold text-white">Regenerate World Keys</h3>
        <span className="ml-auto text-[10px] bg-red-500/10 border border-red-500/20 text-red-400 px-2 py-0.5 rounded-full">CB Admin only</span>
      </div>

      <div className="px-5 pb-5 space-y-4">
        <p className="text-xs text-slate-500 leading-relaxed">
          Use this when a world's API key or HMAC secret has been lost or compromised. New credentials are generated and shown
          only once — the game server must be updated immediately.
        </p>

        {newKeys ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4" />
              Keys regenerated — save these now, they will not be shown again
            </div>
            <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2.5">
              ⚠ The old API key and HMAC secret are now invalid. Update the game server before any transactions can be processed.
            </p>
            {[
              { label: 'New API Key',     value: newKeys.apiKey,    key: 'apiKey' },
              { label: 'New HMAC Secret', value: newKeys.hmacSecret, key: 'hmac' },
            ].map(({ label, value, key }) => (
              <div key={key}>
                <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">{label}</p>
                <div className="flex items-center gap-2 bg-[#0b1120] border border-white/8 rounded-xl px-3 py-2.5">
                  <Key className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                  <span className="font-mono text-xs text-cyan-300 break-all flex-1">{value}</span>
                  <button onClick={() => copyToClipboard(value, key)} className="flex-shrink-0 text-slate-600 hover:text-cyan-400 transition-colors">
                    {copied === key ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            ))}
            <button onClick={handleReset} className="w-full py-2.5 rounded-xl text-sm text-slate-400 border border-white/8 hover:border-white/15 hover:text-slate-300 transition-all">
              Done — regenerate another world
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <select
                value={selectedWorldId}
                onChange={(e) => { setSelectedWorldId(e.target.value); setConfirmed(false); }}
                className="w-full text-sm bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-slate-300 focus:outline-none focus:border-red-500/40 appearance-none pr-8"
              >
                <option value="">— Select world to regenerate keys —</option>
                {worlds.map((w) => (
                  <option key={w.id} value={w.id}>{w.name} ({w.currencySymbol})</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600 pointer-events-none" />
            </div>

            {selectedWorldId && !confirmed && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="flex items-start gap-2 bg-red-500/8 border border-red-500/20 rounded-xl px-3 py-3"
              >
                <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-red-300 font-medium">This will immediately invalidate the current API key and HMAC secret.</p>
                  <p className="text-xs text-slate-500 mt-0.5">The world's game server will be unable to process events until it is updated with the new credentials.</p>
                </div>
              </motion.div>
            )}

            {selectedWorldId && (
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="w-3.5 h-3.5 accent-red-500"
                  />
                  <span className="text-xs text-slate-400">I understand this action cannot be undone</span>
                </label>
                <button
                  onClick={() => regenMutation.mutate(selectedWorldId)}
                  disabled={!confirmed || regenMutation.isPending}
                  className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-xl bg-red-500/15 border border-red-500/30 text-red-400 hover:bg-red-500/25 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {regenMutation.isPending
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</>
                    : <><RefreshCw className="w-3.5 h-3.5" /> Regenerate Keys</>}
                </button>
              </div>
            )}

            {regenMutation.isError && (
              <p className="text-red-400 text-xs">{extractApiError(regenMutation.error, 'Failed to regenerate keys. Please try again.')}</p>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function CentralBankPage() {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.role);
  const isReadOnly = role === 'ADMIN_READONLY';
  const [selectedWorldId, setSelectedWorldId] = useState('');
  const [seedAmount, setSeedAmount] = useState('');
  const [seedNote, setSeedNote] = useState('');
  const [showSeedForm, setShowSeedForm] = useState(false);
  const [historyWorldId, setHistoryWorldId] = useState('');
  const [editCbRevShare, setEditCbRevShare] = useState(false);
  const [cbRevShareInput, setCbRevShareInput] = useState('');

  const { data: reserve, isLoading: reserveLoading } = useQuery({
    queryKey: ['cb-reserve'],
    queryFn: () => api.get('/central-bank/reserve').then((r) => r.data),
    refetchInterval: 30_000,
  });

  const { data: cbConfig, isLoading: cbConfigLoading } = useQuery<{ tipRevSharePct: string }>({
    queryKey: ['cb-config'],
    queryFn: () => api.get('/central-bank/config').then((r) => r.data),
  });

  const cbConfigMutation = useMutation({
    mutationFn: (tipRevSharePct: string) =>
      api.patch('/central-bank/config', { tipRevSharePct }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cb-config'] });
      setEditCbRevShare(false);
    },
  });

  const { data: worlds = [] } = useQuery({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  const { data: history = [], isFetching: historyFetching } = useQuery({
    queryKey: ['seeding-history', historyWorldId],
    queryFn: () => api.get(`/central-bank/worlds/${historyWorldId}/seeding-history`).then((r) => r.data).catch(() => []),
    enabled: !!historyWorldId,
  });

  // CB world-currency holdings
  const { data: holdings, refetch: refetchHoldings } = useQuery<{
    ukcReserve: Record<string, string> | null;
    worldCurrencyHoldings: { id: string; worldId: string; currency: string; balance: string; updatedAt: string }[];
  }>({
    queryKey: ['cb-holdings'],
    queryFn: () => api.get('/central-bank/holdings').then((r) => r.data),
    refetchInterval: 60_000,
  });

  const [convertHoldingWorldId, setConvertHoldingWorldId] = useState('');
  const [convertHoldingDir, setConvertHoldingDir] = useState<'TO_UKC' | 'FROM_UKC'>('TO_UKC');
  const [convertHoldingAmt, setConvertHoldingAmt] = useState('');
  const [convertHoldingSuccess, setConvertHoldingSuccess] = useState<Record<string, string> | null>(null);

  const { data: convertHoldingRate } = useQuery<{ ukcPerUnit: number } | null>({
    queryKey: ['exchange-rate', convertHoldingWorldId],
    queryFn: () => convertHoldingWorldId ? api.get(`/exchange/rates/${convertHoldingWorldId}`).then((r) => r.data).catch(() => null) : Promise.resolve(null),
    enabled: !!convertHoldingWorldId,
  });

  const convertHoldingMutation = useMutation({
    mutationFn: (data: { worldId: string; amount: string; direction: string }) =>
      api.post('/central-bank/convert-holding', data).then((r) => r.data),
    onSuccess: (data) => {
      setConvertHoldingSuccess(data);
      setConvertHoldingAmt('');
      qc.invalidateQueries({ queryKey: ['cb-holdings'] });
      qc.invalidateQueries({ queryKey: ['cb-reserve'] });
    },
  });

  const seedMutation = useMutation({
    mutationFn: (data: { worldId: string; amount: string; note?: string }) =>
      api.post('/central-bank/seed-world', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cb-reserve'] });
      if (selectedWorldId) qc.invalidateQueries({ queryKey: ['seeding-history', selectedWorldId] });
      setSeedAmount('');
      setSeedNote('');
      setShowSeedForm(false);
    },
  });

  const fmt = (n: number | string | undefined) =>
    n == null ? '--' : Number(n).toLocaleString('en-GB', { maximumFractionDigits: 2 });

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/15 to-emerald-500/15 border border-cyan-500/20 flex items-center justify-center">
            <Landmark className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Central Bank</h2>
            <p className="text-xs text-slate-600">UKC reserve, supply management &amp; world seeding</p>
          </div>
        </div>
        {!isReadOnly && (
          <button
            onClick={() => setShowSeedForm(!showSeedForm)}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl btn-primary"
          >
            <Sprout className="w-3.5 h-3.5" /> Seed World
          </button>
        )}
      </div>

      {/* Reserve Donut Chart + stats */}
      {reserveLoading ? (
        <div className="flex items-center gap-2 text-slate-500 text-xs py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading reserve data...
        </div>
      ) : (
        <>
          {reserve && (
            <div className="flex flex-col items-center mb-6">
              <ReserveDonutChart reserve={reserve} />
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard label="Total Supply" value={`${fmt(reserve?.totalSupply)} UKC`} icon={BarChart2} color="text-cyan-400" />
          <StatCard label="Circulating Supply" value={`${fmt(reserve?.circulatingSupply)} UKC`} icon={TrendingUp} color="text-purple-400" />
          <StatCard
            label="Reserve Balance"
            value={`${fmt(reserve?.reserveBalance)} UKC`}
            sub={reserve?.totalSupply ? `${((Number(reserve.reserveBalance) / Number(reserve.totalSupply)) * 100).toFixed(1)}% of total` : undefined}
            icon={Landmark}
            color="text-emerald-400"
          />
          <StatCard
            label="CB Revenue Balance"
            value={`${fmt(reserve?.cbRevenueBalance)} UKC`}
            sub="Earned from rev share &amp; gifts"
            icon={TrendingUp}
            color="text-rose-400"
          />
          <StatCard
            label="Available for Seeding"
            value={`${fmt(reserve?.totalAvailableForSeeding)} UKC`}
            sub="Reserve + CB revenue combined"
            icon={Sprout}
            color="text-teal-400"
          />
          <StatCard
            label="Last Reconciled"
            value={reserve?.lastReconciledAt ? new Date(reserve.lastReconciledAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '--'}
            sub={reserve?.lastReconciledAt ? formatDate(reserve.lastReconciledAt) : undefined}
            icon={Clock}
            color="text-amber-400"
          />
        </div>
        </>
      )}

      {/* ── CB Tip Revenue Share Config ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card overflow-hidden"
        style={{ borderColor: 'rgba(139,92,246,0.2)' }}
      >
        <div className="h-px bg-gradient-to-r from-transparent via-purple-500/25 to-transparent" />
        <div className="px-5 py-4 flex items-center gap-2.5">
          <Percent className="w-4 h-4 text-purple-400" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">Tip / Gift Revenue Share — CB Cut</h3>
            <p className="text-[10px] text-slate-600 mt-0.5">
              % of the world's revenue share chunk that goes to the Central Bank on every P2P tip or gift. The remainder flows to the partner world's OWNER_REVENUE account.
            </p>
          </div>
          <span className="text-[10px] bg-purple-500/10 border border-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full ml-auto">CB Admin only</span>
        </div>

        <div className="px-5 pb-5">
          {cbConfigLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-xs"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading config…</div>
          ) : !editCbRevShare ? (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-purple-400">{parseFloat(cbConfig?.tipRevSharePct ?? '30').toFixed(2)}</span>
                <span className="text-sm text-slate-500 mt-1">%</span>
              </div>
              <p className="text-xs text-slate-600 flex-1">of the world rev-share chunk sent to CB reserve per tip</p>
              {!isReadOnly && (
                <button
                  onClick={() => { setCbRevShareInput(cbConfig?.tipRevSharePct ?? '30'); setEditCbRevShare(true); }}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-purple-300 px-3 py-1.5 rounded-lg border border-white/8 hover:border-purple-500/25 transition-all"
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" /> Edit
                </button>
              )}
            </div>
          ) : (
            <form
              className="flex items-end gap-3"
              onSubmit={(e) => { e.preventDefault(); cbConfigMutation.mutate(cbRevShareInput); }}
            >
              <div className="flex-1 max-w-xs">
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">CB Cut % (0–100)</label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={cbRevShareInput}
                    onChange={(e) => setCbRevShareInput(e.target.value)}
                    required
                    className={inputCls}
                    autoFocus
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-600">%</span>
                </div>
              </div>
              <button
                type="submit"
                disabled={cbConfigMutation.isPending}
                className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2.5 rounded-xl bg-purple-500/15 border border-purple-500/25 text-purple-300 hover:bg-purple-500/25 transition-all"
              >
                {cbConfigMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save
              </button>
              <button
                type="button"
                onClick={() => setEditCbRevShare(false)}
                className="text-xs text-slate-400 px-3 py-2.5 rounded-xl border border-white/8 hover:border-white/15 transition-all"
              >
                Cancel
              </button>
              {cbConfigMutation.isError && (
                <p className="text-red-400 text-[10px]">{(cbConfigMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Save failed'}</p>
              )}
            </form>
          )}
        </div>
      </motion.div>

      {/* Seed form */}
      <AnimatePresence>
        {showSeedForm && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="glass-card p-6 space-y-4 overflow-hidden"
            style={{ borderColor: 'rgba(16,185,129,0.2)' }}
            onSubmit={(e) => {
              e.preventDefault();
              seedMutation.mutate({
                worldId: selectedWorldId,
                amount: seedAmount,
                note: seedNote || undefined,
              });
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
            <div className="flex items-center gap-2">
              <Sprout className="w-4 h-4 text-emerald-400" />
              <p className="text-sm font-semibold text-white">Seed World P2E Pool</p>
            </div>
            <p className="text-xs text-slate-500">
              Transfers UKC from the Central Bank reserve into a world&apos;s Play-to-Earn pool account. This increases the world&apos;s available reward budget.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">World</label>
                <div className="relative">
                  <select
                    value={selectedWorldId}
                    onChange={(e) => setSelectedWorldId(e.target.value)}
                    required
                    className={`${inputCls} appearance-none pr-8`}
                  >
                    <option value="">Select world...</option>
                    {(worlds as Record<string, string>[]).map((w) => (
                      <option key={w['id']} value={w['id']}>
                        {w['name']} ({w['currencySymbol']})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Amount (UKC)</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={seedAmount}
                  onChange={(e) => setSeedAmount(e.target.value)}
                  placeholder="e.g. 500000"
                  required
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Note (optional)</label>
                <input
                  value={seedNote}
                  onChange={(e) => setSeedNote(e.target.value)}
                  placeholder="e.g. Initial launch budget"
                  className={inputCls}
                />
              </div>
            </div>

            {reserve?.totalAvailableForSeeding && seedAmount && Number(seedAmount) > Number(reserve.totalAvailableForSeeding) && (
              <div className="flex items-start gap-2 text-amber-400 text-xs">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                Amount exceeds total available for seeding ({fmt(reserve.totalAvailableForSeeding)} UKC: reserve + CB revenue)
              </div>
            )}

            {seedMutation.isError && (
              <p className="text-red-400 text-xs">{(seedMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Seed failed'}</p>
            )}
            {seedMutation.isSuccess && (
              <p className="text-emerald-400 text-xs flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Seeded successfully</p>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowSeedForm(false)}
                className="text-xs text-slate-400 px-4 py-2 rounded-xl border border-white/8 hover:border-white/15 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={seedMutation.isPending}
                className="flex items-center gap-2 text-xs font-semibold px-5 py-2 rounded-xl bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/25 transition-all"
              >
                {seedMutation.isPending ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Seeding...</>
                ) : (
                  <><Sprout className="w-3.5 h-3.5" /> Confirm Seed</>
                )}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* CB World Currency Holdings */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
        className="glass-card overflow-hidden"
      >
        <div className="h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-white">World Currency Holdings</h3>
            <span className="text-[10px] text-slate-600 ml-1">Received from partner worlds as gifts — convert to UKC or hold</span>
          </div>
          <button onClick={() => refetchHoldings()} className="text-xs text-slate-600 hover:text-slate-400 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {(!holdings?.worldCurrencyHoldings || holdings.worldCurrencyHoldings.length === 0) ? (
          <p className="px-5 py-10 text-center text-xs text-slate-700">No world currency holdings. When partner worlds send gifts to the CB, they appear here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  {['World', 'Currency', 'Balance', 'Last Updated', ...(isReadOnly ? [] : [''])].map((h, i) => (
                    <th key={i} className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-widest text-slate-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {holdings.worldCurrencyHoldings.map((h) => {
                  const w = (worlds as { id: string; name: string; currencySymbol: string }[]).find((ww) => ww.id === h.worldId);
                  return (
                    <tr key={h.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }} className="table-row-hover">
                      <td className="px-5 py-3 text-slate-300">{w?.name ?? h.worldId}</td>
                      <td className="px-5 py-3 font-mono text-amber-400 font-semibold">{h.currency}</td>
                      <td className="px-5 py-3 font-mono text-white">{Number(h.balance).toLocaleString('en-GB', { maximumFractionDigits: 4 })}</td>
                      <td className="px-5 py-3 text-slate-600">{formatDateTime(h.updatedAt)}</td>
                      {!isReadOnly && (
                        <td className="px-5 py-3">
                          <button
                            onClick={() => { setConvertHoldingWorldId(h.worldId); setConvertHoldingDir('TO_UKC'); setConvertHoldingSuccess(null); }}
                            className="text-[10px] px-2 py-1 rounded-lg border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                          >
                            Convert → UKC
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Inline convert form */}
        {!isReadOnly && convertHoldingWorldId && (() => {
          const activeHolding = (holdings?.worldCurrencyHoldings ?? []).find((h) => h.worldId === convertHoldingWorldId);
          const spendCurrency = convertHoldingDir === 'TO_UKC' ? (activeHolding?.currency ?? '?') : 'UKC';
          const receiveCurrency = convertHoldingDir === 'TO_UKC' ? 'UKC' : (activeHolding?.currency ?? '?');
          const availableAmt = convertHoldingDir === 'TO_UKC'
            ? (activeHolding ? Number(activeHolding.balance) : 0)
            : (holdings ? Number(holdings.ukcReserve?.cbRevenueBalance ?? 0) : 0);
          const rate = convertHoldingRate?.ukcPerUnit ?? null;
          const previewAmt = (() => {
            if (!convertHoldingAmt || !rate) return null;
            const n = parseFloat(convertHoldingAmt);
            if (isNaN(n) || n <= 0) return null;
            return convertHoldingDir === 'TO_UKC'
              ? (n * rate).toFixed(8)
              : (n / rate).toFixed(8);
          })();
          return (
            <div className="px-5 py-4 border-t border-white/5 bg-white/2">
              <p className="text-xs font-semibold text-slate-300 mb-3">
                Convert holding for <span className="text-amber-400">{(worlds as { id: string; name: string }[]).find((w) => w.id === convertHoldingWorldId)?.name}</span>
              </p>
              {convertHoldingSuccess ? (
                <div className="flex items-center gap-2 text-xs text-emerald-400">
                  <Check className="w-3.5 h-3.5" />
                  Converted {convertHoldingSuccess['sendAmount']} {convertHoldingSuccess['sendCurrency']} → {convertHoldingSuccess['receiveAmount']} {convertHoldingSuccess['receiveCurrency']}
                  <button onClick={() => { setConvertHoldingSuccess(null); setConvertHoldingWorldId(''); }} className="ml-2 text-slate-600 hover:text-slate-400">✕</button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <select
                      value={convertHoldingDir}
                      onChange={(e) => { setConvertHoldingDir(e.target.value as 'TO_UKC' | 'FROM_UKC'); setConvertHoldingAmt(''); }}
                      className="text-xs bg-[#0b1120] border border-white/10 rounded-lg px-2 py-1.5 text-slate-300 focus:outline-none focus:border-cyan-500/40"
                    >
                      <option value="TO_UKC">World currency → UKC</option>
                      <option value="FROM_UKC">UKC → World currency holding</option>
                    </select>
                  </div>
                  <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                    Amount ({spendCurrency}){' '}
                    <span className="text-slate-700 normal-case">
                      — available: {availableAmt.toLocaleString('en-GB', { maximumFractionDigits: 4 })} {spendCurrency}
                    </span>
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" step="any"
                      placeholder={`e.g. 10 ${spendCurrency}`}
                      max={availableAmt}
                      value={convertHoldingAmt}
                      onChange={(e) => setConvertHoldingAmt(e.target.value)}
                      className="w-36 text-xs bg-[#0b1120] border border-white/10 rounded-lg px-2 py-1.5 text-slate-300 focus:outline-none focus:border-cyan-500/40"
                    />
                    <button
                      disabled={convertHoldingMutation.isPending || !convertHoldingAmt}
                      onClick={() => convertHoldingMutation.mutate({ worldId: convertHoldingWorldId, amount: convertHoldingAmt, direction: convertHoldingDir })}
                      className="text-xs px-3 py-1.5 rounded-lg btn-primary disabled:opacity-50"
                    >
                      {convertHoldingMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Convert'}
                    </button>
                    <button onClick={() => setConvertHoldingWorldId('')} className="text-xs text-slate-600 hover:text-slate-400">Cancel</button>
                  </div>
                  {convertHoldingMutation.isError && (
                    <p className="text-red-400 text-[10px] mt-1">{extractApiError(convertHoldingMutation.error, 'Convert failed')}</p>
                  )}
                  {/* Rate info + preview */}
                  {rate && (
                    <p className="text-[10px] text-slate-600">
                      Rate: 1 {activeHolding?.currency ?? '?'} = {rate} UKC
                    </p>
                  )}
                  {!rate && convertHoldingWorldId && (
                    <p className="text-[10px] text-amber-600">No exchange rate set for this world</p>
                  )}
                  {previewAmt && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 p-3 rounded-xl bg-cyan-500/[0.05] border border-cyan-500/15"
                    >
                      <div className="text-center flex-1">
                        <p className="font-mono text-sm font-bold text-white">{parseFloat(convertHoldingAmt).toFixed(4)} {spendCurrency}</p>
                        <p className="text-[9px] text-slate-600 mt-0.5">You spend</p>
                      </div>
                      <ArrowRightLeft className="w-3.5 h-3.5 text-cyan-500/50 flex-shrink-0" />
                      <div className="text-center flex-1">
                        <p className="font-mono text-sm font-bold text-emerald-400">{parseFloat(previewAmt).toFixed(4)} {receiveCurrency}</p>
                        <p className="text-[9px] text-slate-600 mt-0.5">You receive</p>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </motion.div>

      {/* Seeding history */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass-card overflow-hidden"
      >
        <div className="h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-white">Seeding History</h3>
          </div>
          <div className="relative">
            <select
              value={historyWorldId}
              onChange={(e) => setHistoryWorldId(e.target.value)}
              className="text-xs bg-[#0b1120] border border-white/10 rounded-lg px-3 py-1.5 text-slate-300 focus:outline-none focus:border-cyan-500/40 appearance-none pr-7"
            >
              <option value="">Select world to view history</option>
              {(worlds as Record<string, string>[]).map((w) => (
                <option key={w['id']} value={w['id']}>
                  {w['name']} ({w['currencySymbol']})
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-600 pointer-events-none" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                {['Amount (UKC)', 'Note', 'Seeded At', 'Seeded By'].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-widest text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!historyWorldId ? (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-slate-700">
                    Select a world above to view its seeding history
                  </td>
                </tr>
              ) : historyFetching ? (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center">
                    <Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-600" />
                  </td>
                </tr>
              ) : history.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-slate-700">
                    No seeding events recorded for this world
                  </td>
                </tr>
              ) : (history as Record<string, unknown>[]).map((h, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }} className="table-row-hover">
                  <td className="px-5 py-3 text-emerald-400 font-mono font-semibold">
                    +{Number(h['amount']).toLocaleString('en-GB')}
                  </td>
                  <td className="px-5 py-3 text-slate-400">{(h['metadata'] as Record<string, string>)?.['note'] ?? <span className="text-slate-700">—</span>}</td>
                  <td className="px-5 py-3 text-slate-500">
                    {h['timestamp'] ? formatDateTime(h['timestamp'] as string) : '—'}
                  </td>
                  <td className="px-5 py-3 text-slate-600 font-mono text-[10px]">{(h['metadata'] as Record<string, string>)?.['adminUserId'] ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* ── Key Regeneration ── */}
      {!isReadOnly && <WorldKeyRegenSection worlds={worlds as { id: string; name: string; currencySymbol: string }[]} />}
    </div>
  );
}
